import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Grid,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  MdAccessTime,
  MdLogin,
  MdLogout,
  MdArrowForward,
  MdCheckCircle,
} from "react-icons/md";
import {
  clockIn,
  clockOut,
  getTodayStatus,
} from "services/company/attendanceManagement/attendanceRecords/attendanceRecordsService";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";
import usePermissions from "hooks/usePermissions";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { getSecureLocalItem } from "utils/secureSessionStorage";

const MyAttendanceCard = ({ myTodayRecord, user, onRefresh }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  // Permissions & Role
  const { hasPermission, permissions } = usePermissions();
  const currentUser = useSelector(selectCurrentUser) || user;
  const userRole = getSecureLocalItem("role") || currentUser?.role || currentUser?.role_name || user?.role || "Company";
  const isCompanyRole = userRole?.toLowerCase() === "company";
  const isEmployeeRole = userRole?.toLowerCase() === "employee";

  const canViewAttendanceRecords =
    hasPermission("attendance_records.view") ||
    hasPermission("attendance.view") ||
    (permissions && permissions.some((p) => typeof p === "string" && p.startsWith("attendance"))) ||
    !isEmployeeRole;

  const [actionLoading, setActionLoading] = useState(false);
  const [personalStatus, setPersonalStatus] = useState(null);
  const [fetchingStatus, setFetchingStatus] = useState(true);

  // Fetch today's attendance status from GET /v1/api/attendance/today-status
  const fetchPersonalTodayStatus = useCallback(async () => {
    try {
      setFetchingStatus(true);
      const res = await getTodayStatus();
      if (res?.meta?.status === true || res?.status === 200) {
        const data = res.data || res;
        setPersonalStatus(data);
      }
    } catch (err) {
      console.warn("Failed to fetch today status:", err);
    } finally {
      setFetchingStatus(false);
    }
  }, []);

  useEffect(() => {
    const token = getSecureLocalItem("token");
    if (token && !isCompanyRole) {
      fetchPersonalTodayStatus();
    }
  }, [fetchPersonalTodayStatus, isCompanyRole]);

  if (isCompanyRole) {
    return null;
  }

  // Combine personal status from API with prop record
  const activeRecord = personalStatus?.check_in
    ? personalStatus
    : myTodayRecord || personalStatus;

  const formatTime = (dateStr) => {
    if (!dateStr) return "--:--";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const isCheckedIn = Boolean(activeRecord?.check_in && !activeRecord?.check_out);
  const isCheckedOut = Boolean(activeRecord?.check_in && activeRecord?.check_out);
  const notPunched = !activeRecord?.check_in;

  const calculateHoursLogged = () => {
    if (!activeRecord?.check_in) return "0 hrs 0 mins";

    if (activeRecord?.working_minutes) {
      const hrs = Math.floor(activeRecord.working_minutes / 60);
      const mins = activeRecord.working_minutes % 60;
      return `${hrs} hrs ${mins} mins`;
    }

    const startTime = new Date(activeRecord.check_in).getTime();
    const endTime = activeRecord.check_out
      ? new Date(activeRecord.check_out).getTime()
      : new Date().getTime();
    const diffMs = Math.max(0, endTime - startTime);
    const totalMins = Math.floor(diffMs / (1000 * 60));
    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return `${hrs} hrs ${mins} mins`;
  };

  // Helper to obtain GPS coordinates or fallback
  const getCoordinates = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: 0, longitude: 0 });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        },
        () => {
          resolve({ latitude: 0, longitude: 0 });
        },
        { timeout: 5000 }
      );
    });
  };

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      const coords = await getCoordinates();
      const response = await clockIn(coords);

      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(response?.meta?.message || "Checked in successfully!");
        await fetchPersonalTodayStatus();
        if (onRefresh) onRefresh();
      } else {
        const errorMsg = response?.meta?.message || "Check-in failed";
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error("Check-in error:", err);
      showErrorToast(err?.meta?.message || err?.message || "Check-in failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const coords = await getCoordinates();
      const response = await clockOut(coords);

      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(response?.meta?.message || "Checked out successfully!");
        await fetchPersonalTodayStatus();
        if (onRefresh) onRefresh();
      } else {
        const errorMsg = response?.meta?.message || "Check-out failed";
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error("Check-out error:", err);
      showErrorToast(err?.meta?.message || err?.message || "Check-out failed");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 1,
        background: isDark ? "#111827" : "#ffffff",
        border: 1, borderColor: "divider",
        boxShadow: isDark
          ? "0 4px 20px rgba(0, 0, 0, 0.25)"
          : "0 4px 20px rgba(15, 23, 42, 0.05)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        {/* Header Title & Live Status Chip */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: isDark ? alpha("#f59e0b", 0.2) : "#fef3c7",
                color: "#d97706",
                display: "flex",
                alignItems: "center",
              }}
            >
              <MdAccessTime size={20} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
              My Attendance Status Today
            </Typography>
          </Stack>

          <Chip
            label={isCheckedIn ? "Checked In" : isCheckedOut ? "Shift Ended" : "Not Checked In"}
            size="small"
            sx={{
              ml: "auto",
              fontWeight: 700,
              fontSize: "0.75rem",
              backgroundColor: isCheckedIn
                ? isDark ? alpha("#10b981", 0.2) : "#d1fae5"
                : isCheckedOut
                  ? isDark ? alpha("#6366f1", 0.2) : "#e0e7ff"
                  : isDark ? alpha("#f43f5e", 0.2) : "#ffe4e6",
              color: isCheckedIn ? "#059669" : isCheckedOut ? "#4338ca" : "#e11d48",
            }}
          />
        </Stack>

        {/* Attendance Details Grid */}
        <Box
          sx={{
            p: 2.5,
            borderRadius: 1,
            bgcolor: isDark ? alpha("#1e293b", 0.5) : "#f8fafc",
            border: 1, borderColor: "divider",
            mb: 2.5,
          }}
        >
          {fetchingStatus && !activeRecord?.check_in ? (
            <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} sx={{ color: "primary.main" }} />
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <MdLogin color="#10b981" size={18} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      Check In Time
                    </Typography>
                  </Stack>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "text.primary", fontFamily: "monospace" }}>
                    {formatTime(activeRecord?.check_in)}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <MdLogout color={activeRecord?.check_out ? "#f43f5e" : "#94a3b8"} size={18} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      Check Out Time
                    </Typography>
                  </Stack>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "text.primary", fontFamily: "monospace" }}>
                    {formatTime(activeRecord?.check_out)}
                  </Typography>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 2,
                  pt: 1.5,
                  borderTop: `1px dashed ${isDark ? "#334155" : "#e2e8f0"}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Total Hours Logged Today:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: "primary.main", fontFamily: "monospace", fontSize: "0.85rem" }}>
                  {calculateHoursLogged()}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Check In / Check Out Interactive Action Button */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {notPunched && (
          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={actionLoading}
            onClick={handleCheckIn}
            startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <MdLogin />}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 800,
              py: 1.2,
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
              color: "#ffffff",
              "&:hover": {
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                boxShadow: "0 6px 18px rgba(16, 185, 129, 0.45)",
              },
            }}
          >
            {actionLoading ? "Punching In..." : "Check In Now"}
          </Button>
        )}

        {isCheckedIn && (
          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={actionLoading}
            onClick={handleCheckOut}
            startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <MdLogout />}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 800,
              py: 1.2,
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              boxShadow: "0 4px 14px rgba(239, 68, 68, 0.35)",
              color: "#ffffff",
              "&:hover": {
                background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                boxShadow: "0 6px 18px rgba(239, 68, 68, 0.45)",
              },
            }}
          >
            {actionLoading ? "Punching Out..." : "Check Out Now"}
          </Button>
        )}

        {isCheckedOut && (
          <Button
            variant="outlined"
            fullWidth
            disabled
            startIcon={<MdCheckCircle color="#6366f1" />}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 700,
              py: 1,
              borderColor: isDark ? "rgba(99, 102, 241, 0.3)" : "#c7d2fe",
              color: "primary.main",
            }}
          >
            Shift Completed Today
          </Button>
        )}

        {canViewAttendanceRecords && (
          <Button
            variant="text"
            fullWidth
            size="small"
            endIcon={<MdArrowForward />}
            onClick={() => navigate("/company/attendance-records")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "text.secondary",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            View Attendance History
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default MyAttendanceCard;
